/**
 * Page context for Soteria
 * ------------------------
 * Describes what the advisor is currently looking at, so the assistant can
 * resolve deictic references ("this client", "her", "why did this
 * underperform?") without an extra lookup turn.
 *
 * Derived purely from the pathname — no data fetching — so it is cheap enough
 * to recompute on every turn. The entity id is the one already in the URL; the
 * agent resolves it through its normal read tools (it is never trusted as
 * authorization, which stays server-side and org-scoped).
 */

/** The kinds of record a route can be focused on. */
export type PageEntityType = 'client' | 'strategy' | 'liveModel' | 'benchmark';

export interface SoteriaPageContext {
  /** The current pathname, e.g. "/clients/abc-123". */
  route: string;
  /** Human-readable area name the agent can echo back, e.g. "Client detail". */
  area: string;
  /** The record this page is about, when the route carries one. */
  entity?: { type: PageEntityType; id: string };
  /**
   * The report tab in view (the `?tab=` value), set only on the backtest
   * report. It tells the assistant WHICH charts are on screen — performance vs
   * risk vs constituents vs turnover — so "explain these plots" resolves to the
   * right section without asking which tab.
   */
  tab?: string;
}

interface Matcher {
  /** Regex over the pathname; capture group 1 (when present) is the entity id. */
  test: RegExp;
  area: string;
  entityType?: PageEntityType;
  /**
   * The route carries a `?tab=` that selects what's on screen (the backtest
   * report). When set, describePage surfaces the caller-supplied tab; other
   * routes ignore it.
   */
  hasTab?: boolean;
}

// Ordered most-specific first — the first match wins.
const MATCHERS: Array<Matcher> = [
  // Clients
  {
    test: /^\/clients\/([^/]+)\/open-account\/?$/,
    area: 'Account opening wizard',
    entityType: 'client',
  },
  { test: /^\/clients\/open-account\/?$/, area: 'Account opening wizard' },
  {
    test: /^\/clients\/([^/]+)\/?$/,
    area: 'Client detail',
    entityType: 'client',
  },
  { test: /^\/clients\/?$/, area: 'Client directory' },

  // Strategies
  {
    test: /^\/strategies\/([^/]+)\/live\/[^/]+\/?$/,
    area: 'Live model',
    entityType: 'strategy',
  },
  {
    test: /^\/strategies\/([^/]+)\/report\/?$/,
    area: 'Strategy backtest report',
    entityType: 'strategy',
    hasTab: true,
  },
  {
    test: /^\/strategies\/benchmarks\/([^/]+)\/?$/,
    area: 'Benchmark detail',
    entityType: 'benchmark',
  },
  {
    test: /^\/strategies\/strategy-creation-form(\/.*)?$/,
    area: 'Strategy builder',
  },
  { test: /^\/strategies\/?$/, area: 'Strategies list' },

  // Everything else
  { test: /^\/dashboard\/?$/, area: 'Dashboard' },
  { test: /^\/portfolio\/?$/, area: 'Portfolio' },
  { test: /^\/team\/?$/, area: 'Team' },
  { test: /^\/settings\/?$/, area: 'Settings' },
  { test: /^\/soteria\/?$/, area: 'Soteria' },
  { test: /^\/onboarding\/?$/, area: 'Onboarding' },
  { test: /^\/docs\/?$/, area: 'Docs' },
  { test: /^\/help\/?$/, area: 'Help' },
];

/**
 * Describe the page at `pathname` for the assistant. `tab` is the report
 * `?tab=` value; it is surfaced only on routes that carry one (the backtest
 * report), so the assistant knows which charts are on screen. Callers on other
 * pages can omit it.
 *
 * Unknown routes still return a context (area "Unknown") so the agent always
 * knows the raw path rather than getting nothing at all.
 */
export function describePage(
  pathname: string,
  tab?: string,
): SoteriaPageContext {
  const route = pathname || '/';
  for (const m of MATCHERS) {
    const match = m.test.exec(route);
    if (!match) continue;
    const id = m.entityType ? match[1] : undefined;
    return {
      route,
      area: m.area,
      ...(m.entityType && id ? { entity: { type: m.entityType, id } } : {}),
      ...(m.hasTab && tab ? { tab } : {}),
    };
  }
  return { route, area: 'Unknown' };
}
